/**
 * „Ostatnie sesje" — the Przegląd's session table (KANON „Karty zadań", layout A,
 * `canvas.js` `sessionsTable`, 24 September 2026). Every decision about WHICH rows and what they say
 * is `model/sessionTable`'s; this draws them.
 *
 * A real `<table>`: eight rows of eight columns are read across as well as down, and a screen reader
 * gets the column header with every cell. Each session's NAME is the row's one control — a button
 * stretched over the row, so a click anywhere on it opens that session, while Tab stops once per row
 * and the button's accessible name is the words on it. A missed row opens nothing: there is no
 * execution to open.
 */

import { useTranslation } from 'react-i18next';

import { wellbeingBand, type WellbeingLevels, type WellbeingSignal } from '~/entities/client';
import { useHomeFormats } from '~/features/attention-queue';
import { sessionOpenerKey, type SessionOpenIntent } from '~/shared/lib/sessionOpener';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { SessionTableRow, SessionTableSessionRow } from '../model/sessionTable';
import styles from './SessionTable.module.css';

const BAND_CLASS = {
  low: String(styles.barLow),
  middling: '',
  high: String(styles.barHigh),
} as const;

/** The top of each self-report's scale, for the bar's length: mood is 1–10, sleep is hours. */
const BAR_TOP: Readonly<Record<WellbeingSignal, number>> = { mood: 10, sleep: 9 };

interface SessionTableProps {
  readonly rows: readonly SessionTableRow[];
  /** The client's initials, for the disc that says they wrote something under a session. */
  readonly clientInitials: string;
  /** Under what a sleep or an energy bar is low: the trainer's own levels, read by the tab. */
  readonly levels: WellbeingLevels;
  readonly onOpenSession: (workoutExecutionId: number, intent: SessionOpenIntent) => void;
}

export function SessionTable({ rows, clientInitials, levels, onOpenSession }: SessionTableProps) {
  const { t } = useTranslation();
  const formats = useHomeFormats();
  const none = t('web:clientDetail.sessions.noValue');

  return (
    <div className={styles.card}>
      <table className={styles.table} aria-label={t('web:clientDetail.sessions.tableLabel')}>
        <thead>
          <tr className={styles.head}>
            <th scope="col" className={styles.colDate}>{t('web:clientDetail.sessions.col.date')}</th>
            <th scope="col">{t('web:clientDetail.sessions.col.session')}</th>
            <th scope="col" className={styles.colSets}>{t('web:clientDetail.sessions.col.sets')}</th>
            <th scope="col" className={styles.colTonnage}>{t('web:clientDetail.sessions.col.tonnage')}</th>
            <th scope="col" className={styles.colTime}>{t('web:clientDetail.sessions.col.time')}</th>
            <th scope="col" className={styles.colBar}>{t('web:clientDetail.sessions.col.mood')}</th>
            <th scope="col" className={styles.colBar}>{t('web:clientDetail.sessions.col.sleep')}</th>
            <th scope="col" className={`${String(styles.colNotes)} ${String(styles.end)}`}>
              {t('web:clientDetail.sessions.col.notes')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) =>
            row.kind === 'missed' ? (
              <tr key={row.key} className={`${String(styles.row)} ${String(styles.missed)}`}>
                <td className={styles.date}>{formats.weekdayDate(row.day)}</td>
                <th scope="row" className={styles.name}>
                  <span className={styles.nameText}>
                    {row.name ?? t('web:clientDetail.sessions.unnamedMissed')}
                  </span>
                </th>
                <td>
                  <span className={styles.missedChip}>{t('web:clientDetail.sessions.missed')}</span>
                </td>
                <td />
                <td />
                <td />
                <td />
                <td />
              </tr>
            ) : (
              <SessionRow
                key={row.key}
                row={row}
                none={none}
                clientInitials={clientInitials}
                levels={levels}
                onOpenSession={onOpenSession}
              />
            ),
          )}
        </tbody>
      </table>
      <p className={styles.foot}>
        <span className={styles.legend}>
          <span aria-hidden="true" className={`${String(styles.face)} ${String(styles.faceYou)}`}>
            {t('web:clientDetail.sessions.you')}
          </span>
          {t('web:clientDetail.sessions.youWrote')}
        </span>
        <span className={styles.legend}>
          <span aria-hidden="true" className={`${String(styles.face)} ${String(styles.faceClient)}`}>
            {clientInitials}
          </span>
          {t('web:clientDetail.sessions.clientWrote')}
        </span>
        <span className={styles.legend}>
          <span aria-hidden="true" className={`${String(styles.face)} ${String(styles.faceNone)}`} />
          {t('web:clientDetail.sessions.noNote')}
        </span>
        <span className={styles.footnote}>{t('web:clientDetail.sessions.footnote')}</span>
      </p>
    </div>
  );
}

function SessionRow({
  row,
  none,
  clientInitials,
  levels,
  onOpenSession,
}: {
  readonly row: SessionTableSessionRow;
  readonly none: string;
  readonly clientInitials: string;
  readonly levels: WellbeingLevels;
  readonly onOpenSession: (workoutExecutionId: number, intent: SessionOpenIntent) => void;
}) {
  const { t } = useTranslation();
  const formats = useHomeFormats();
  const delta = row.tonnageDeltaPct;

  return (
    <tr className={styles.row}>
      <td className={styles.date}>{formats.weekdayDate(row.day)}</td>
      <th scope="row" className={styles.name}>
        <span className={styles.nameLine}>
          <button
            type="button"
            className={styles.open}
            // The address the screen focuses back to when the session panel closes.
            data-session-opener={sessionOpenerKey(row.workoutExecutionId, 'read')}
            onClick={() => {
              onOpenSession(row.workoutExecutionId, 'read');
            }}
          >
            {row.name ?? t('web:clientDetail.sessions.unnamed')}
          </button>
          {/* A record is LIME, and the word travels with the star so the hue is never the only
            * channel. */}
          {row.record ? (
            <span className={styles.star} title={t('web:clientDetail.sessions.record')}>
              <span aria-hidden="true">★</span>
              <span className={hidden.srOnly}>{t('web:clientDetail.sessions.record')}</span>
            </span>
          ) : null}
        </span>
        {row.mode === null ? null : (
          <span className={styles.sub}>{t(`web:clientDetail.sessions.mode.${row.mode}`)}</span>
        )}
      </th>
      <td className={styles.num}>
        <b className={styles.sets}>
          {row.setsDone}/{row.setsTotal}
        </b>
      </td>
      <td className={styles.num}>
        {row.tonnageKg === null ? (
          none
        ) : (
          <>
            {t('web:clientDetail.sessions.tonnage', { value: formats.decimal(row.tonnageKg / 1000) })}
            {delta === null ? null : (
              <span
                className={`${String(styles.delta)} ${delta > 0 ? String(styles.up) : delta < 0 ? String(styles.down) : ''}`}
                title={deltaWords(delta)}
              >
                <span aria-hidden="true">
                  {delta > 0 ? '↑' : delta < 0 ? '↓' : '='}
                  {delta === 0 ? '' : `${String(Math.abs(delta))}%`}
                </span>
                <span className={hidden.srOnly}>{deltaWords(delta)}</span>
              </span>
            )}
          </>
        )}
      </td>
      <td className={styles.num}>
        {row.durationMin === null ? none : t('web:clientDetail.sessions.minutes', { count: row.durationMin })}
      </td>
      <td className={styles.num}>{bar(row.mood, 'mood', String(row.mood))}</td>
      <td className={styles.num}>
        {bar(
          row.sleepHours,
          'sleep',
          row.sleepHours === null ? '' : t('web:clientDetail.sessions.hours', { value: formats.decimal(row.sleepHours) }),
        )}
      </td>
      <td className={styles.end}>
        <span className={styles.faces}>
          {row.clientWrote ? (
            <span className={`${String(styles.face)} ${String(styles.faceClient)}`} title={t('web:clientDetail.sessions.clientWrote')}>
              <span aria-hidden="true">{clientInitials}</span>
              <span className={hidden.srOnly}>{t('web:clientDetail.sessions.clientWrote')}</span>
            </span>
          ) : null}
          {row.reviewed ? (
            <span className={`${String(styles.face)} ${String(styles.faceYou)}`} title={t('web:clientDetail.sessions.youWrote')}>
              <span aria-hidden="true">{t('web:clientDetail.sessions.you')}</span>
              <span className={hidden.srOnly}>{t('web:clientDetail.sessions.youWrote')}</span>
            </span>
          ) : (
            <span className={`${String(styles.face)} ${String(styles.faceNone)}`} title={t('web:clientDetail.sessions.noNote')}>
              <span className={hidden.srOnly}>{t('web:clientDetail.sessions.noNote')}</span>
            </span>
          )}
        </span>
      </td>
    </tr>
  );

  function deltaWords(value: number): string {
    return value > 0
      ? t('web:clientDetail.sessions.deltaUp', { value })
      : value < 0
        ? t('web:clientDetail.sessions.deltaDown', { value: Math.abs(value) })
        : t('web:clientDetail.sessions.deltaSame');
  }

  /** A self-report as a short bar and its figure; the band's colour is never the only channel. */
  function bar(value: number | null, signal: WellbeingSignal, figure: string) {
    if (value === null) return none;
    return (
      <>
        <span aria-hidden="true" className={`${String(styles.bar)} ${String(styles[signal])}`}>
          <i
            className={`${String(styles.barFill)} ${BAND_CLASS[wellbeingBand(value, signal, levels)]}`}
            style={{ inlineSize: `${String(Math.min(100, Math.round((value / BAR_TOP[signal]) * 100)))}%` }}
          />
        </span>
        {figure}
      </>
    );
  }
}
